import { useNavigate } from 'react-router-dom';

interface ReactAdminErrorProps {
  error: Error;
  errorInfo?: React.ErrorInfo;
  resetErrorBoundary?: (args?: any) => void;
}

const isDevelopment = import.meta.env.MODE === "development";
const isStaging = import.meta.env.MODE === "staging";

const ReactAdminError = ({ error, resetErrorBoundary }: ReactAdminErrorProps) => {
  const navigate = useNavigate();

  const handleTryAgain = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    }
    navigate('/');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      <div
        style={{
          maxWidth: '500px',
          width: '100%',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          padding: '40px',
          textAlign: 'center'
        }}
      >
        {/* Car Icon */}
        <div
          style={{
            width: '80px',
            height: '80px',
            background: '#dc2626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 30px',
            position: 'relative'
          }}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
            <path d="M5,11L6.5,6.5H17.5L19,11M17.5,16A1.5,1.5 0 0,1 16,14.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 19,14.5A1.5,1.5 0 0,1 17.5,16M6.5,16A1.5,1.5 0 0,1 5,14.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 8,14.5A1.5,1.5 0 0,1 6.5,16M18.92,6C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.28,5.42 5.08,6L3,12V20A1,1 0 0,0 4,21H5A1,1 0 0,0 6,20V19H18V20A1,1 0 0,0 19,21H20A1,1 0 0,0 21,20V12L18.92,6Z" />
          </svg>
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              width: '24px',
              height: '24px',
              background: '#f59e0b',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
            </svg>
          </div>
        </div>

        <h1
          style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 16px 0'
          }}
        >
          Application Error
        </h1>

        <p
          style={{
            fontSize: '16px',
            color: '#6b7280',
            margin: '0 0 30px 0',
            lineHeight: '1.5'
          }}
        >
          Something went wrong in our automotive management system. Our technical team has been notified.
        </p>

        {/* Show error details only in dev/staging */}
        {(isDevelopment || isStaging) && (
          <div
            style={{
              marginBottom: '30px',
              padding: '20px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              textAlign: 'left'
            }}
          >
            <h3
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#b91c1c',
                margin: '0 0 12px 0'
              }}
            >
              Technical Details
            </h3>
            <div style={{ marginBottom: '12px' }}>
              <h4
                style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  margin: '0 0 4px 0'
                }}
              >
                Error Message:
              </h4>
              <p
                style={{
                  fontSize: '14px',
                  color: '#dc2626',
                  fontFamily: 'monospace',
                  background: '#fee2e2',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #fecaca',
                  margin: '0',
                  wordBreak: 'break-word'
                }}
              >
                {error.message}
              </p>
            </div>
            {error.stack && (
              <div>
                <h4
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#374151',
                    margin: '0 0 4px 0'
                  }}
                >
                  Stack Trace:
                </h4>
                <div
                  style={{
                    background: '#f3f4f6',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db',
                    maxHeight: '160px',
                    overflow: 'auto'
                  }}
                >
                  <pre
                    style={{
                      fontSize: '12px',
                      color: '#4b5563',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      margin: '0',
                      wordBreak: 'break-word'
                    }}
                  >
                    {error.stack}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center'
          }}
        >
          <button
            onClick={handleTryAgain}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#2563eb')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#3b82f6')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
            </svg>
            Try Again
          </button>
          <button
            onClick={handleGoHome}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#f9fafb')}
            onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
            </svg>
            Go Home
          </button>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '32px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}
        >
          <p
            style={{
              fontSize: '12px',
              color: '#9ca3af',
              margin: '0'
            }}
          >
            Auttom - Your automotive management solution
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReactAdminError;
